// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
    Link,
} from '@fluentui/react-components';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useConfig } from '../../data';
import { GitHubLogo } from '../../icons/sign-in';
import { useAppStyles } from '../../styles';
import { TrackContext } from '../common';
import { CancelButton, CloseButton, ConfirmationField, ConfirmButton } from '../dialogs';

// Unlink GitHub Account dialog properties.
export type UnlinkGithubDialogProps = Partial<DialogProps> & {
    onConfirm: () => void;
};

// Unlink GitHub Account dialog.
export const UnlinkGithubDialog: FC<UnlinkGithubDialogProps> = (props) => {
    const { onConfirm, open } = props;
    const [confirmed, setConfirmed] = useState(false);
    const styles = useAppStyles();
    const { githubAppId } = useConfig();
    return (
        <TrackContext.Provider value={{ container: 'link-github-dialog' }}>
            <Dialog {...props}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle action={<CloseButton />}>
                            <FormattedMessage
                                defaultMessage="Unlink your GitHub account"
                                description="Unlink to GitHub dialog title"
                            />
                        </DialogTitle>
                        <DialogContent className={styles.flexRowCentered}>
                            <GitHubLogo className={styles.dialogImage} />
                            <div className={styles.dialogContent}>
                                <div>
                                    <FormattedMessage
                                        defaultMessage="Unlinking your GitHub account will prevent you from creating and managing custom learning activities."
                                        description="Unlink to GitHub dialog content"
                                    />
                                </div>
                                <div>
                                    <FormattedMessage
                                        description="Title for a list of actions to unlink a GitHub account."
                                        defaultMessage="Follow these steps to remove a link to your GitHub account from VS Code for Education:"
                                    />
                                    <ul>
                                        <li>
                                            <FormattedMessage
                                                description="An item in the list of of actions to unlink a GitHub account."
                                                defaultMessage="Open the {APP_LINK} in GitHub security settings and click 'Revoke access', then confirm the action."
                                                values={{
                                                    APP_LINK: (
                                                        <Link
                                                            href={`https://github.com/settings/connections/applications/${githubAppId}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <FormattedMessage
                                                                description="Link text on the unlink GitHub account dialog to open GitHub."
                                                                defaultMessage="VS Code for Education application"
                                                            />
                                                        </Link>
                                                    ),
                                                }}
                                            />
                                        </li>
                                        <li>
                                            <FormattedMessage
                                                description="An item in the list of of actions to unlink a GitHub account."
                                                defaultMessage="Complete the operation by clicking the button below."
                                            />
                                        </li>
                                    </ul>
                                </div>
                                <ConfirmationField
                                    dialogOpen={open}
                                    onChange={setConfirmed}
                                    confirmationText="UNLINK"
                                />
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <CancelButton />
                            <ConfirmButton className={styles.buttonDanger} onClick={onConfirm} disabled={!confirmed}>
                                <FormattedMessage defaultMessage="Unlink" description="Unlink to GitHub button" />
                            </ConfirmButton>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </TrackContext.Provider>
    );
};

export default UnlinkGithubDialog;
